import { useMemo, useRef } from 'react'
import { deSerialize, getQuery } from '../_helpers/firestoreHelper'
import { collection, doc, onSnapshot, serverTimestamp, setDoc as setDocFb, writeBatch, updateDoc as updateDocFb, addDoc as addDocFb, deleteDoc as deleteDocFb, getDocs as getDocsFb } from 'firebase/firestore'
import { USER_COLLECTION } from '../_constants/globals'
import { db, listenerRefs } from '../firebase'
import { useDispatch, useSelector } from 'react-redux'
import { omit, pickBy } from 'lodash'
import { actions } from '../store/slices/firestoreSlice'
import { isNil } from 'lodash/lang'


export const useFirestore = (path, options) => {

  const lastDocRef = useRef()
  const profile = useSelector(state => state.profile.data)
  const dispatch = useDispatch()
  const serializedDocs = useSelector(state => state.firestore[options?.storeAs || path])
  const deserializedDocs = useMemo(() => serializedDocs?.map(deSerialize), [serializedDocs])

  const listen = listenOptions => {
    options ? console.info('Listen', path, options) : console.info('Listen', path)
    const query = getQuery(path, listenOptions)
    dispatch(actions.loading())
    const listener = onSnapshot(query, {}, snap => {
      if (snap.empty) return dispatch(actions?.success(options?.storeAs || path, []))
      else {
        const data = snap.docs.map(doc => ({ id: doc.id, ...omit(doc.data(), listenOptions?.omit || []) }))
        dispatch(actions?.success(options?.storeAs || path, data))
        if (listenOptions?.lazyLoad) lastDocRef.current = snap.docs.pop()
      }
    })
    listenerRefs[options?.listenerName || path] = { unsubscribe: listener }
  }

  const fetch = options =>
    getDocsFb(getQuery(path, options)).then(snap =>
      snap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
    )

  const getDocs = () => deserializedDocs

  const getDocRef = id => id ? doc(db, path, id) : doc(collection(db, path))

  const addDoc = data =>
    addDocFb(collection(db, path), {
      ...data,
      _createdAtTime: serverTimestamp(),
      _createdByRef: doc(db, USER_COLLECTION, profile.id),
    })
      .then(res => console.info(`${path} created with id: ${res.id}`))
      .catch(e => console.log('Error creating document', e))

  const updateDoc = (id, data) =>
    updateDocFb(doc(db, path, id), {
      ...data,
      _updatedAtTime: serverTimestamp(),
      _updatedByRef: doc(db, USER_COLLECTION, profile.id),
    })
      .then(() => console.info(`${path} updated, id: ${id}`))
      .catch(e => console.error(`Error updating document with id: ${id}`, e))

  const setDoc = async (id, data) => {
    const ref = getDocRef(id)
    await setDocFb(ref, pickBy({
      ...data,
      _createdAtTime: !id && serverTimestamp(),
      _createdByRef: !id && doc(db, USER_COLLECTION, profile.id),
      _updatedAtTime: !!id && serverTimestamp(),
      _updatedByRef: !!id && doc(db, USER_COLLECTION, profile.id),
    }), { merge: true })
    console.info(`${path} set, id: ${ref.id}`)
    return ref
  }

  const deleteDoc = id =>
    deleteDocFb(doc(db, path, id))
      .then(() => console.info(`${path} deleted, id: ${id}`))
      .catch(e => console.error(`Error deleting document with id: ${id}`, e))

  /**
   * @param {[string]} ids
   */
  const batchDeleteDocAction = ids => {
    const batch = writeBatch(db)
    ids.forEach(id => batch.delete(doc(db, path, id)))
    return batch.commit()
  }

  /**
   * @param {string} collectionStr
   * @param {object[]} data
   * @returns {Promise<void>}
   */
  const batchSetAction = (collectionStr, data) => {
    const batch = writeBatch(db)
    data.forEach(document => batch.set(document.id ? doc(db, collectionStr, document.id.toString()) : doc(collection(db, collectionStr)), pickBy({
      ...document,
      id: null,
      _createdAtTime: !document?.id && serverTimestamp(),
      _createdByRef: !document?.id && doc(db, USER_COLLECTION, profile.id),
      _updatedAtTime: document?.id && serverTimestamp(),
      _updatedByRef: document?.id && doc(db, USER_COLLECTION, profile.id),
    }, val => !isNil(val)), { merge: true }))
    return batch.commit()
  }

  const getBatch = () => writeBatch(db)

  const unsubscribe = listenerName => listenerRefs[listenerName || options?.storeAs || path]?.unsubscribe()

  return { listen, fetch, getDocs, getDocRef, addDoc, updateDoc, setDoc, deleteDoc, batchDeleteDocAction, batchSetAction, getBatch, unsubscribe }
}
